import React, { useState, useEffect } from "react";
import { CognitoUserPool } from "amazon-cognito-identity-js";
import { X, Lock, Eye, EyeOff, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";

// Cognito pool — same envs the rest of the app uses.
const userPool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_USER_POOL_ID,
  ClientId: process.env.REACT_APP_CLIENT_ID,
});

const PasswordField = ({ label, value, onChange, show, onToggle, autoComplete, colors, disabled }) => (
  <div className="space-y-2">
    <label className={`text-sm font-semibold ${colors.textSecondary}`}>{label}</label>
    <div className="relative">
      <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${colors.textMuted}`} />
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        disabled={disabled}
        className={`w-full pl-10 pr-10 py-2.5 rounded-xl border ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} ${colors.inputPlaceholder} focus:outline-none focus:ring-2 ${colors.inputFocus} transition-colors disabled:opacity-50`}
        placeholder="••••••••"
      />
      <button
        type="button"
        onClick={onToggle}
        tabIndex={-1}
        className={`absolute right-3 top-1/2 -translate-y-1/2 ${colors.textMuted} hover:${colors.textSecondary} transition-colors`}
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  </div>
);

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const { colors } = useTheme();

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Reset everything whenever the modal closes/opens.
  useEffect(() => {
    if (!isOpen) {
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowOld(false);
      setShowNew(false);
      setShowConfirm(false);
      setLoading(false);
      setError("");
      setSuccess(false);
    }
  }, [isOpen]);

  const validate = () => {
    if (!oldPassword) return "Mevcut şifreyi girin.";
    if (!newPassword) return "Yeni şifreyi girin.";
    if (newPassword.length < 8) return "Yeni şifre en az 8 karakter olmalı.";
    if (!/[A-Z]/.test(newPassword)) return "Yeni şifrede en az bir büyük harf olmalı.";
    if (!/[a-z]/.test(newPassword)) return "Yeni şifrede en az bir küçük harf olmalı.";
    if (!/[0-9]/.test(newPassword)) return "Yeni şifrede en az bir rakam olmalı.";
    if (newPassword !== confirmPassword) return "Yeni şifre ve tekrarı eşleşmiyor.";
    if (newPassword === oldPassword) return "Yeni şifre, mevcut şifreyle aynı olamaz.";
    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const currentUser = userPool.getCurrentUser();
    if (!currentUser) {
      setError("Oturum bulunamadı. Lütfen tekrar giriş yapın.");
      return;
    }

    setLoading(true);

    // changePassword needs an active session — refresh tokens first.
    currentUser.getSession((sessErr, session) => {
      if (sessErr || !session || !session.isValid()) {
        setLoading(false);
        setError("Oturum süresi dolmuş. Lütfen çıkış yapıp tekrar giriş yapın.");
        return;
      }

      currentUser.changePassword(oldPassword, newPassword, (err) => {
        setLoading(false);
        if (err) {
          let msg;
          switch (err.code) {
            case "NotAuthorizedException":
              msg = "Mevcut şifre yanlış.";
              break;
            case "InvalidPasswordException":
              msg = "Yeni şifre kurallara uymuyor. " + (err.message || "");
              break;
            case "LimitExceededException":
              msg = "Çok fazla deneme yapıldı. Lütfen bir süre sonra tekrar deneyin.";
              break;
            default:
              msg = err.message || "Şifre değiştirilemedi.";
          }
          setError(msg);
          return;
        }
        setSuccess(true);
        // Auto-close after a brief success display.
        setTimeout(() => {
          onClose();
        }, 1500);
      });
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4">
      <div className={`w-full max-w-md ${colors.cardBg} rounded-2xl shadow-2xl border ${colors.cardBorder} max-h-[90vh] flex flex-col overflow-hidden`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${colors.border}`}>
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Lock className="w-5 h-5 text-blue-500" />
            </div>
            <h2 className={`text-lg font-bold ${colors.textPrimary}`}>Şifre Değiştir</h2>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className={`p-2 rounded-lg ${colors.bgHover} disabled:opacity-50 transition-colors`}
          >
            <X className={`w-5 h-5 ${colors.textMuted}`} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-4 overflow-y-auto custom-scrollbar min-h-0 flex-1">
          {success ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-3">
              <div className="p-3 rounded-full bg-green-500/10">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <p className={`text-sm font-semibold ${colors.textPrimary}`}>
                Şifreniz başarıyla değiştirildi.
              </p>
            </div>
          ) : (
            <>
              <PasswordField
                label="Mevcut Şifre"
                value={oldPassword}
                onChange={setOldPassword}
                show={showOld}
                onToggle={() => setShowOld((v) => !v)}
                autoComplete="current-password"
                colors={colors}
                disabled={loading}
              />
              <PasswordField
                label="Yeni Şifre"
                value={newPassword}
                onChange={setNewPassword}
                show={showNew}
                onToggle={() => setShowNew((v) => !v)}
                autoComplete="new-password"
                colors={colors}
                disabled={loading}
              />
              <PasswordField
                label="Yeni Şifre (Tekrar)"
                value={confirmPassword}
                onChange={setConfirmPassword}
                show={showConfirm}
                onToggle={() => setShowConfirm((v) => !v)}
                autoComplete="new-password"
                colors={colors}
                disabled={loading}
              />

              <div className={`text-[11px] ${colors.textTertiary} leading-relaxed`}>
                En az 8 karakter, bir büyük harf, bir küçük harf ve bir rakam içermeli.
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700 dark:text-red-300">{error}</p>
                </div>
              )}
            </>
          )}
        </form>

        {/* Footer */}
        {!success && (
          <div className={`flex justify-end gap-3 p-6 border-t ${colors.border}`}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className={`px-5 py-2 rounded-lg border ${colors.btnSecondaryBorder} ${colors.btnSecondaryText} ${colors.btnSecondaryBg} ${colors.btnSecondaryHover} text-sm font-semibold transition-colors disabled:opacity-50`}
            >
              İptal
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              <span>{loading ? "Kaydediliyor..." : "Şifreyi Değiştir"}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChangePasswordModal;
